import { useEffect } from 'react';
import { useNavigation } from 'expo-router';

import { useResponsive } from '@sg/garnish';

import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../../../components';
import { type BaseProduct, type ProductDetailsMappedContent } from '../../ProductDetails.types';
import { ProductDetailsReadySM } from './ProductDetailsReady.sm';
import { ProductDetailsReadyXS } from './ProductDetailsReady.xs';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsReady = (props: Props) => {
  const {
    baseProduct,
    productCost,
    restaurantData,
    deliveryDetailsData,
    orderChannel,
    locationName,
    content,
    hasNoSelectedLocation,
    isUnavailableProduct,
    isAddingToBag,
    navigateBack,
    navigateToLocationSearch,
    onAddToBag,
  } = props;

  // ─────────────────────────────────────────────────────────────────────

  const { minWidth } = useResponsive();
  const navigation = useNavigation();
  const setOptions = navigation.setOptions;

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    setOptions({ headerShown: minWidth.isSM });
  }, [minWidth.isSM, setOptions]);

  // ─── SM+ ───────────────────────────────────────────────────────────────────

  if (minWidth.isSM) {
    return (
      <ProductDetailsReadySM
        baseProduct={baseProduct}
        productCost={productCost}
        restaurantData={restaurantData}
        deliveryDetailsData={deliveryDetailsData}
        orderChannel={orderChannel}
        locationName={locationName}
        content={content}
        hasNoSelectedLocation={hasNoSelectedLocation}
        isUnavailableProduct={isUnavailableProduct}
        isAddingToBag={isAddingToBag}
        navigateBack={navigateBack}
        navigateToLocationSearch={navigateToLocationSearch}
        onAddToBag={onAddToBag}
      />
    );
  }

  // ─── XS ────────────────────────────────────────────────────────────────────

  return (
    <ProductDetailsReadyXS
      baseProduct={baseProduct}
      content={content}
      hasNoSelectedLocation={hasNoSelectedLocation}
      isUnavailableProduct={isUnavailableProduct}
      isAddingToBag={isAddingToBag}
      navigateBack={navigateBack}
      navigateToCustomization={onAddToBag}
      navigateToLocationSearch={navigateToLocationSearch}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  baseProduct: BaseProduct;
  productCost: number | undefined;
  restaurantData: MenuContentRestaurantData | null | undefined;
  deliveryDetailsData: MenuContentDeliveryDetailsData | undefined | null;
  orderChannel: MenuContentOrderChannel;
  hasNoSelectedLocation: boolean;
  isUnavailableProduct: boolean;
  locationName: string;
  content: ProductDetailsMappedContent;
  isAddingToBag: boolean;
  navigateBack: () => void;
  navigateToLocationSearch: () => void;
  onAddToBag: () => void;
};
